import {
  CompensationReviewCampaignStatus,
  CompensationReviewRecommendationStatus,
  type Prisma,
  UserRole,
} from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { chain, omit } from "~/lib/lodash";
import { and } from "~/lib/prismaHelpers";
import { isIn } from "~/lib/utils";
import { type FetchCampaignsInput } from "~/pages/api/compensation-review/campaigns/fetch-campaigns";

export const fetchCampaigns = async (ctx: AppContext, input: FetchCampaignsInput) => {
  const user = getRequiredUser(ctx);

  const campaigns = await ctx.prisma.compensationReviewCampaign.findMany({
    where: {
      companyId: user.companyId,
    },
    orderBy: [{ status: "desc" }, { name: "asc" }], // important for Intercom training, don't change without consulting @HarveyThePooka404
    select: {
      id: true,
      name: true,
      startDate: true,
      endDate: true,
      status: true,
      admins: { select: { userId: true } },
      _count: {
        select: {
          employees: {
            where: { eligibilities: { some: {} } },
          },
        },
      },
    },
  });

  const campaignIds = campaigns.map((campaign) => campaign.id);

  const commonGroupByOptions = {
    by: ["campaignId"],
    where: { campaignId: { in: campaignIds } },
    _count: { _all: true },
  } satisfies Prisma.CompensationReviewRecommendationGroupByArgs;

  const whereSubmitted = {
    status: { in: [CompensationReviewRecommendationStatus.SUBMITTED, CompensationReviewRecommendationStatus.LOCKED] },
  } satisfies Prisma.CompensationReviewRecommendationWhereInput;

  const whereReviewer = {
    reviewer: { externalEmployeeId: user.permissions.externalEmployeeId ?? -1 },
  } satisfies Prisma.CompensationReviewRecommendationWhereInput;

  const recommendationCountPerCampaign = await ctx.prisma.compensationReviewRecommendation.groupBy({
    ...commonGroupByOptions,
  });

  const submittedRecommendationCountPerCampaign = await ctx.prisma.compensationReviewRecommendation.groupBy({
    ...commonGroupByOptions,
    where: and([commonGroupByOptions.where, whereSubmitted]),
  });

  const reviewerRecommendationCountPerCampaign = await ctx.prisma.compensationReviewRecommendation.groupBy({
    ...commonGroupByOptions,
    where: and([commonGroupByOptions.where, whereReviewer]),
  });

  const submittedReviewerRecommendationCountPerCampaign = await ctx.prisma.compensationReviewRecommendation.groupBy({
    ...commonGroupByOptions,
    where: and([commonGroupByOptions.where, whereSubmitted, whereReviewer]),
  });

  type RecommendationCountPerCampaign = typeof recommendationCountPerCampaign;

  return chain(campaigns)
    .map((campaign) => {
      const isSuperAdmin = user.isSuperAdmin;
      const isCampaignAdmin = campaign.admins.map(({ userId }) => userId).includes(user.id);
      const isFiguresAdmin = user.permissions.role === UserRole.ADMIN;

      return {
        ...campaign,
        isCampaignAdmin: isSuperAdmin || isCampaignAdmin || isFiguresAdmin,
      };
    })
    .filter((campaign) => {
      if (!input.onlyActive) return true;

      if (campaign.isCampaignAdmin) {
        return isIn(campaign.status, [
          CompensationReviewCampaignStatus.CONFIGURATION,
          CompensationReviewCampaignStatus.SANDBOX,
          CompensationReviewCampaignStatus.IN_PROGRESS,
          CompensationReviewCampaignStatus.CLOSING,
        ]);
      }

      return campaign.status === CompensationReviewCampaignStatus.IN_PROGRESS;
    })
    .map((campaign) => {
      const getCount = (count: RecommendationCountPerCampaign) => {
        return count.find(({ campaignId }) => campaignId === campaign.id)?._count._all ?? 0;
      };

      const reviewerProgress = {
        count: getCount(submittedReviewerRecommendationCountPerCampaign),
        total: getCount(reviewerRecommendationCountPerCampaign),
      };

      const overallProgress = campaign.isCampaignAdmin
        ? {
            count: getCount(submittedRecommendationCountPerCampaign),
            total: getCount(recommendationCountPerCampaign),
          }
        : null;

      return {
        ...omit(campaign, "employees", "recommendations"),
        reviewerProgress,
        overallProgress,
      };
    })
    .filter((campaign) => {
      const hasRecommendations = campaign.reviewerProgress.total > 0;

      const campaignIsSandbox = campaign.status === CompensationReviewCampaignStatus.SANDBOX;
      const campaignIsStarted = isIn(campaign.status, [
        CompensationReviewCampaignStatus.IN_PROGRESS,
        CompensationReviewCampaignStatus.CLOSED,
      ]);

      if (campaign.isCampaignAdmin) return true;

      if (campaignIsSandbox) return !!ctx.impersonation?.user;

      return campaignIsStarted && hasRecommendations;
    })
    .orderBy((campaign) => {
      return campaign.status === CompensationReviewCampaignStatus.IN_PROGRESS ? 0 : 1;
    })
    .value();
};

export type FetchCampaignsResult = AsyncReturnType<typeof fetchCampaigns>;
